import type { ComponentProps } from 'react'

import { useTheme } from '@emotion/react'

import { BaseButton } from '../base-button'
import { ButtonRippleAnimation } from '../button-ripple-animation'

import { TextButtonTypography } from './text-button-typography'
import * as styles from './text-button.styles'
import type { Colors, Sizes, Variants } from './text-button.styles'

type Props = {
  color?: Colors
  size?: Sizes
  fullWidth?: boolean
  noTypography?: boolean
  variant?: Variants
} & ComponentProps<typeof BaseButton>

export const TextButton = ({
  color = 'primary',
  size = 'medium',
  fullWidth = false,
  noTypography = false,
  children,
  variant,
  ...buttonProps
}: Props) => {
  const theme = useTheme()

  const rippleColorMap: Record<Colors, string> = {
    contained: theme.palette.primary.reversedContrast,
    primary: theme.palette.primary.reversedContrast,
    outlined: theme.palette.primary.lowContrast,
    secondary: theme.palette.primary.lowContrast,
    negative: theme.palette.error.lowContrast,
    ghost: theme.palette.primary.lowContrast,
    link: theme.palette.primary.mainContrast,
    'reverse_contained': theme.palette.primary.lowContrast,
    'reverse_outlined': theme.palette.primary.lowContrast,
    'contained_cms': theme.palette.primary.reversedContrast,
    'outlined_cms': theme.palette.primary.lowContrast,
  }

  const buttonVariant = variant || color

  const buttonStyles = [
    styles.root(theme),
    styles.sizes(theme)[size],
    styles.colors(theme)[color],
    variant && styles.variants(theme)[variant],
    (buttonVariant === 'contained_cms' ||
      buttonVariant === 'outlined_cms') &&
      styles.cmsSize(theme, size),
    (buttonVariant === 'reverse_contained' ||
      buttonVariant === 'reverse_outlined' ||
      buttonVariant === 'reversedPrimary' ||
      buttonVariant === 'reversedSecondary' ||
      buttonVariant === 'inverted' ||
      buttonVariant === 'outlinedInverted') &&
      styles.reversedSize(theme, size),
    fullWidth && styles.fullWidth,
    styles.presentation(theme, size, buttonVariant),
  ]

  return (
    <ButtonRippleAnimation color={rippleColorMap[color]}>
      {({ rippleButtonStyles, ref, ripples }) => (
        <BaseButton
          ref={ref}
          css={[buttonStyles, rippleButtonStyles]}
          data-testid="text-button"
          {...buttonProps}
        >
          {ripples}
          <TextButtonTypography noTypography={noTypography} size={size}>
            {children}
          </TextButtonTypography>
        </BaseButton>
      )}
    </ButtonRippleAnimation>
  )
}
